/**
 * Add stock — design/PROTOTYPE-SPEC.md §5.
 *
 * "Manual pick from the product list (searchable) → quantity, lot, expiry,
 * location → creates a tracked batch."
 *
 * This was the blocking gap: `POST /products/:id/stock` has been built and
 * tested since slice 2, but nothing called it, so a clinic could import its
 * catalogue and then be unable to book in a delivery.
 *
 * Two steps on purpose — pick the product, then describe the delivery. Putting
 * a product search and six batch fields on one screen makes the search look like
 * another field to fill in rather than a choice to make.
 *
 * Barcode scanning is explicitly out of v1; see PROTOTYPE-SPEC §5 for the
 * phone-pairing design intended later.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  addStock,
  createProduct,
  listClassifications,
  listLocations,
  listProducts,
  type Classification,
  type ProductRow,
  type StockLocation,
} from '../lib/api.js';
import { c, field, label as labelStyle, mono, primaryButton, sans } from '../theme.js';
import { Pill, money, penceToPounds, smallButton } from './format.js';
import { ProductForm, toProductBody, type ProductFormValues } from './ProductForm.js';

interface Props {
  onClose: () => void;
  onAdded: () => void | Promise<void>;
  /** Skips the picker when opened from a product's own drawer. */
  presetProduct?: { id: string; name: string; unitCostPence: number } | undefined;
}

export function AddStockModal({ onClose, onAdded, presetProduct }: Props) {
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<{ id: string; name: string; unitCostPence: number } | null>(
    presetProduct ?? null,
  );

  const [qty, setQty] = useState('1');
  const [lot, setLot] = useState('');
  const [expiry, setExpiry] = useState('');
  const [noExpiry, setNoExpiry] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [unitCost, setUnitCost] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [p, l, cl] = await Promise.all([
          listProducts(),
          listLocations(),
          listClassifications(),
        ]);
        setProducts(p.products);
        setLocations(l.locations);
        setClassifications(cl.classifications);
        if (l.locations[0]) setLocationId(l.locations[0].id);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load your products');
      }
    })();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? products.filter((p) =>
          `${p.name} ${p.brand ?? ''} ${p.category ?? ''}`.toLowerCase().includes(q),
        )
      : products;
    return list.slice(0, 40);
  }, [products, search]);

  function choose(p: ProductRow) {
    setChosen({ id: p.id, name: p.name, unitCostPence: p.unitCostPence });
    // Default the batch cost to what the product usually costs; the person
    // booking in can correct it when a delivery was priced differently.
    setUnitCost(p.unitCostPence ? (p.unitCostPence / 100).toString() : '');
    setError(null);
  }

  /**
   * Create the product with its full details, then carry straight on to
   * booking the delivery in. It used to be created with a name alone, which
   * left its brand, category, type and prices to be filled in on another page
   * that nobody thought to look on.
   */
  async function createAndChoose(values: ProductFormValues) {
    setError(null);
    const created = await createProduct(toProductBody(values));
    setChosen({ id: created.id, name: created.name, unitCostPence: values.unitCostPence });
    setUnitCost(values.unitCostPence > 0 ? penceToPounds(values.unitCostPence) : '');
    setCreatingNew(false);
  }

  /** Every category already in use, for the new product's category list. */
  const categories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of products) {
      const name = p.category?.trim();
      if (name && !seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), name);
    }
    return [...seen.values()].sort((x, y) => x.localeCompare(y));
  }, [products]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!chosen) return;

    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1) {
      setError('Enter a whole number of units, at least one');
      return;
    }
    if (!noExpiry && expiry.trim() === '') {
      setError('Enter an expiry date, or tick "This stock does not expire"');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const body: Parameters<typeof addStock>[1] = { qty: n };
      if (lot.trim()) body.lot = lot.trim();
      body.expiry = noExpiry ? null : expiry;
      if (locationId) body.locationId = locationId;
      const cost = unitCost.trim();
      if (cost !== '') {
        const pence = Math.round(Number(cost) * 100);
        if (!Number.isFinite(pence) || pence < 0) {
          setError('Unit cost must be a number');
          setBusy(false);
          return;
        }
        body.unitCostPence = pence;
      }

      await addStock(chosen.id, body);
      await onAdded();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not book that stock in');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,26,22,0.42)',
        zIndex: 80,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)',
          background: c.appBg,
          borderRadius: 18,
          overflow: 'hidden',
          fontFamily: sans,
          color: c.ink,
        }}
      >
        <header
          style={{
            background: c.ink,
            color: c.onDark,
            padding: '18px 22px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <h2
            style={{
              fontFamily: sans, letterSpacing: '-0.03em',
              fontWeight: 300,
              fontSize: 24,
              margin: 0,
              color: c.onDarkBright,
            }}
          >
            Add stock
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: `1px solid ${c.borderDark}`,
              color: c.onDarkSoft,
              borderRadius: 980,
              padding: '6px 12px',
              fontSize: 13,
              fontFamily: sans,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </header>

        <div style={{ padding: '20px 22px 24px' }}>
          {error && (
            <div
              role="alert"
              style={{
                background: c.dangerBg,
                border: `1px solid ${c.dangerBorder}`,
                borderRadius: 12,
                padding: '11px 14px',
                marginBottom: 16,
                fontSize: 13.5,
                color: c.danger,
              }}
            >
              {error}
            </div>
          )}

          {!chosen ? (
            <>
              <label style={labelStyle} htmlFor="add-search">
                Which product?
              </label>
              <input
                id="add-search"
                autoFocus
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCreatingNew(false);
                }}
                placeholder="Search your products"
                style={{ ...field, marginBottom: 12 }}
              />

              <div
                style={{
                  maxHeight: 320,
                  overflowY: 'auto',
                  border: `1px solid ${c.border}`,
                  borderRadius: 12,
                  background: c.card,
                }}
              >
                {matches.length === 0 ? (
                  <div style={{ padding: '18px 16px', fontSize: 13.5, color: c.muted }}>
                    {products.length === 0
                      ? 'You have no products yet.'
                      : `Nothing matches “${search.trim()}”.`}
                  </div>
                ) : (
                  matches.map((p, i) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => choose(p)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        background: 'transparent',
                        border: 'none',
                        borderTop: i === 0 ? 'none' : `1px solid ${c.border}`,
                        padding: '11px 14px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: sans,
                        color: c.ink,
                      }}
                    >
                      <span>
                        <span style={{ fontSize: 14, fontWeight: 500 }}>{p.name}</span>
                        <span style={{ display: 'block', fontSize: 12, color: c.muted, marginTop: 2 }}>
                          {p.qtyOnHand} on hand · {money(p.unitCostPence)} each
                        </span>
                      </span>
                      <Pill tone={p.status.stock}>{p.status.stockLabel}</Pill>
                    </button>
                  ))
                )}
              </div>

              {/* A delivery often contains something not yet tracked. Making the
                  person leave, create a product, and come back loses the delivery. */}
              {search.trim() !== '' && (
                <div style={{ marginTop: 14 }}>
                  {creatingNew ? (
                    <div
                      style={{
                        background: c.card,
                        border: `1px solid ${c.border}`,
                        borderRadius: 14,
                        padding: '16px 16px 18px',
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 12 }}>
                        New product
                      </div>
                      <ProductForm
                        initial={{ name: search.trim() }}
                        classifications={classifications}
                        categories={categories}
                        submitLabel="Create and add stock"
                        onSubmit={createAndChoose}
                        onCancel={() => setCreatingNew(false)}
                      />
                    </div>
                  ) : (
                    <button type="button" onClick={() => setCreatingNew(true)} style={smallButton}>
                      + New product “{search.trim()}”
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <form onSubmit={submit} style={{ display: 'grid', gap: 14 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  background: c.card,
                  border: `1px solid ${c.border}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                }}
              >
                <div>
                  <div style={{ fontSize: 11, textTransform: 'lowercase', fontFamily: mono, color: c.label }}>
                    Booking in
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 500, marginTop: 3 }}>{chosen.name}</div>
                </div>
                {!presetProduct && (
                  <button type="button" onClick={() => setChosen(null)} style={smallButton}>
                    Change
                  </button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle} htmlFor="add-qty">
                    Quantity
                  </label>
                  <input
                    id="add-qty"
                    autoFocus
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    inputMode="numeric"
                    style={field}
                  />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="add-lot">
                    Lot / batch number
                  </label>
                  <input
                    id="add-lot"
                    value={lot}
                    onChange={(e) => setLot(e.target.value)}
                    placeholder="Optional"
                    style={field}
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle} htmlFor="add-expiry">
                  Expiry date
                </label>
                <input
                  id="add-expiry"
                  type="date"
                  value={expiry}
                  disabled={noExpiry}
                  onChange={(e) => setExpiry(e.target.value)}
                  style={{ ...field, opacity: noExpiry ? 0.5 : 1 }}
                />
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    color: c.muted2,
                    marginTop: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={noExpiry}
                    onChange={(e) => setNoExpiry(e.target.checked)}
                  />
                  This stock does not expire
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle} htmlFor="add-location">
                    Location
                  </label>
                  <select
                    id="add-location"
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    style={{ ...field, padding: '0 10px' }}
                  >
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle} htmlFor="add-cost">
                    Unit cost (£)
                  </label>
                  <input
                    id="add-cost"
                    value={unitCost}
                    onChange={(e) => setUnitCost(e.target.value)}
                    inputMode="decimal"
                    placeholder="0.00"
                    style={field}
                  />
                </div>
              </div>

              {/* Cost is per batch, not per product — the reason stock value stays
                  right when a supplier's price changes between deliveries. */}
              <p style={{ fontSize: 12.5, color: c.faint, margin: 0, lineHeight: 1.6 }}>
                Cost is recorded against this delivery, so a price change next time
                will not rewrite what this stock was worth.
              </p>

              <button
                type="submit"
                disabled={busy}
                style={{ ...primaryButton, height: 48, opacity: busy ? 0.6 : 1 }}
              >
                {busy ? 'Booking in…' : 'Book stock in'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
